import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Icons } from "assets/icons";
import { Images } from "assets/images";
import { usePermission } from "hooks/usePermission";
import GridShimmer from "components/common/GridShimmer";
import NoDataFound from "components/common/NoDataFound";
import GridTypeTable from "components/common/GridTypeTable";
import { StatusBadge } from "components/common/StatusBadge";
import PageHeader from "components/common/PageHeader";
import { getLocalizedField } from "utils/utils";
import { getActiveBranches } from "store/slices/developerSettingsSlice";

const DeveloperSettings = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { noRoles } = Icons || {};
  const { noImagePlaceHolder } = Images || {};
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canAction } = usePermission();
  const { canView } = canAction("developer_setting");
  const { canView: canViewBranches } = canAction("branches");

  const { items: branches, loading } = useSelector(
    (state) => state.DeveloperSettings,
  );

  useEffect(() => {
    if (canView) {
      dispatch(getActiveBranches());
    }
  }, [canView]);

  const goToDetails = (branch) => {
    if (!canViewBranches) return;
    navigate(`/developer-settings/branch-details?id=${branch._id}`);
  };

  const renderCardHeader = useCallback(
    (branch) => (
      <>
        <h3 className="branch-name">
          {getLocalizedField(branch?.name, language) || "-"}
        </h3>
        <img
          src={
            branch?.imageUrls?.length ? branch.imageUrls[0] : noImagePlaceHolder
          }
          height={180}
          width="100%"
          alt={getLocalizedField(branch?.name, language) || t("Branch")}
        />
      </>
    ),
    [language],
  );

  const renderCardBody = useCallback(
    (branch) => (
      <div className="stats-grid">
        <div className="stat-item">
          <span className="label">{t("Store No")}</span>
          <span className="value">{branch.storeNo || "-"}</span>
        </div>
      </div>
    ),
    [language, t],
  );

  if (!canView) return null;

  return (
    <div className="page-content active_branches_page">
      <PageHeader title={t("Developer settings")} />

      {loading && <GridShimmer rows={10} columns={4} />}

      {!loading && branches?.length === 0 && (
        <NoDataFound title={t("No active branches found")} icon={noRoles} />
      )}
      <h2>{t("Active Branches")}</h2>

      {!loading && branches?.length > 0 && (
        <GridTypeTable
          data={branches}
          renderCardHeader={renderCardHeader}
          renderCardBody={renderCardBody}
          emptyMessage={t("No active branches found")}
          gridColumns={{ lg: 3, md: 3, sm: 6, xs: 12 }}
          t={t}
          onCardClick={canViewBranches ? goToDetails : undefined}
        />
      )}
    </div>
  );
};

export default DeveloperSettings;
